import React, { useState } from "react";
import {
  Bot,
  User,
  Calendar,
  Clock,
  FileText,
  Building,
  Users,
  AlertCircle,
  CheckCircle2,
  Mail,
  Phone,
  Search,
  Cake,
  Gift,
  Briefcase,
  TrendingUp,
  Tag,
  Shield,
  Compass,
} from "lucide-react";

const ChatMessage = ({ message }) => {
  const isUser = message.sender === "user";
  let data = message.data || {};
  let displayType = message.display_type || "text";
  let messageText = message.text || "";

  // AUTO-RECOVERY & PARSING: If the text is a raw Python dict or JSON (e.g. from previous chat history or raw tool return)
  if (!isUser && typeof messageText === "string" && (messageText.trim().startsWith("{") || messageText.trim().startsWith("["))) {
    try {
      const rawTrim = messageText.trim();
      let parsed = null;
      try {
        parsed = JSON.parse(rawTrim);
      } catch (e1) {
        // Python dict syntax: replace single quotes, None, True, False
        const sanitized = rawTrim
          .replace(/\bNone\b/g, "null")
          .replace(/\bTrue\b/g, "true")
          .replace(/\bFalse\b/g, "false")
          .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, '"$1"');
        parsed = JSON.parse(sanitized);
      }

      if (parsed && typeof parsed === "object") {
        if (parsed.type === "team_members" || parsed.members) {
          displayType = "table_team_members";
          data = {
            ...parsed,
            members: (parsed.members || []).map((m) => ({
              ...m,
              name: m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.emp_code,
              designation: m.designation || m.designation__name || "-",
              department: m.department || m.department__name || "-",
              email: m.email || m.email_id || "-",
              phone: m.phone || m.phone_number || m.mobile_number || "-",
            })),
            total_count: parsed.total_count || parsed.count || (parsed.members ? parsed.members.length : 0),
          };
          messageText = `👥 **Team Members Directory** (Total: **${data.total_count}** members)`;
        } else if (parsed.type === "my_attendance" || parsed.work_hours || parsed.check_in) {
          displayType = "attendance_card";
          data = parsed;
          messageText = `⏱️ **Attendance Summary for ${parsed.date || "Today"}**`;
        } else if (parsed.type === "team_attendance") {
          displayType = "chart_donut";
          data = parsed;
          messageText = `📊 **Team Attendance Summary** (${parsed.date || "Today"})`;
        } else if (parsed.type === "leave_balance") {
          displayType = "metric_cards";
          data = parsed;
          messageText = `🏖️ **Available Leave Balances** (${parsed.emp_code || ""})`;
        } else if (parsed.type === "team_leaves" || parsed.leaves) {
          displayType = "table_leaves";
          data = parsed;
          messageText = `Found **${parsed.count || (parsed.leaves && parsed.leaves.length) || 0}** leave applications.`;
        } else if (parsed.type === "helpdesk_tickets" || parsed.tickets) {
          displayType = "table_tickets";
          data = parsed;
          messageText = `Helpdesk tickets found (${parsed.count || 0}).`;
        } else if (parsed.type === "payslip") {
          displayType = "payslip_card";
          data = parsed;
          messageText = `Payslip Summary for ${parsed.period || ""}`;
        }
      }
    } catch (err) {
      // Keep original text if unparseable
    }
  }

  const [teamSearch, setTeamSearch] = useState("");

  // Helper to format bold markdown, code tags, and line breaks
  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    return rawText.split("\n").map((line, idx) => {
      // Split on **bold** and `code` tags
      const parts = line.split(/(\*\*.*?\*\*|`.*?`)/g);
      return (
        <div key={idx} style={{ minHeight: line.trim() ? "auto" : "8px", marginBottom: "3px" }}>
          {parts.map((part, pIdx) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return (
                <strong key={pIdx} style={{ color: isUser ? "#ffffff" : "#38bdf8", fontWeight: "600" }}>
                  {part.slice(2, -2)}
                </strong>
              );
            }
            if (part.startsWith("`") && part.endsWith("`")) {
              return (
                <code
                  key={pIdx}
                  style={{
                    background: "rgba(0,0,0,0.3)",
                    color: "#38bdf8",
                    padding: "1px 5px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontFamily: "monospace",
                  }}
                >
                  {part.slice(1, -1)}
                </code>
              );
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return (
                <em key={pIdx} style={{ color: "#94a3b8" }}>
                  {part.slice(1, -1)}
                </em>
              );
            }
            return part;
          })}
        </div>
      );
    });
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: "20px",
        gap: "12px",
        alignItems: "flex-start",
      }}
    >
      {!isUser && (
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #3b82f6, #6366f1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
          }}
        >
          <Bot size={20} />
        </div>
      )}

      <div
        style={{
          maxWidth: "85%",
          minWidth: isUser ? "auto" : "320px",
          padding: "16px 20px",
          borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          background: isUser
            ? "linear-gradient(135deg, #2563eb, #1d4ed8)"
            : "linear-gradient(145deg, #1e293b, #0f172a)",
          color: isUser ? "#ffffff" : "#f1f5f9",
          border: isUser ? "none" : "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
          wordBreak: "break-word",
          fontSize: "14px",
          lineHeight: "1.6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
            fontSize: "12px",
            fontWeight: "600",
            opacity: 0.85,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            paddingBottom: "4px",
          }}
        >
          <span>{isUser ? "You" : "AI Assistant"}</span>
        </div>

        {/* Formatted Text Content */}
        <div style={{ marginBottom: "8px" }}>
          {renderFormattedText(messageText)}
        </div>

        {/* 1. WIDGET: Team Members Directory */}
        {displayType === "table_team_members" && data?.members && (
          <div
            style={{
              marginTop: "14px",
              padding: "14px",
              borderRadius: "12px",
              background: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(56, 189, 248, 0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Users size={16} color="#38bdf8" />
                <span style={{ fontSize: "13px", fontWeight: "700", color: "#f8fafc" }}>
                  {data.category_title || "Team Directory"}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    background: "rgba(56, 189, 248, 0.15)",
                    color: "#38bdf8",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontWeight: "600",
                  }}
                >
                  {data.total_count || data.members.length} Total
                </span>
              </div>

              {/* Search filter input */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "rgba(30, 41, 59, 0.8)",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Search size={14} color="#94a3b8" style={{ marginRight: "6px" }} />
                <input
                  type="text"
                  placeholder="Filter name or code..."
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "#f8fafc",
                    fontSize: "12px",
                    width: "140px",
                  }}
                />
              </div>
            </div>

            {/* Members List */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "10px",
                maxHeight: "360px",
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {data.members
                .filter((m) => {
                  const q = teamSearch.toLowerCase();
                  return (
                    !q ||
                    (m.name && m.name.toLowerCase().includes(q)) ||
                    (m.emp_code && m.emp_code.toLowerCase().includes(q)) ||
                    (m.designation && m.designation.toLowerCase().includes(q))
                  );
                })
                .slice(0, 30)
                .map((m, idx) => {
                  const initials = (m.name || m.emp_code || "E")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "10px",
                        background: "rgba(30, 41, 59, 0.6)",
                        border: "1px solid rgba(255, 255, 255, 0.06)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #6366f1, #3b82f6)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "13px",
                          fontWeight: "700",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#f8fafc",
                          }}
                        >
                          <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                            {m.name}
                          </span>
                          <span
                            style={{
                              fontSize: "10px",
                              color: "#38bdf8",
                              background: "rgba(56, 189, 248, 0.12)",
                              padding: "1px 5px",
                              borderRadius: "4px",
                              flexShrink: 0,
                            }}
                          >
                            {m.emp_code}
                          </span>
                        </div>

                        <div
                          style={{
                            fontSize: "11px",
                            color: "#94a3b8",
                            marginTop: "2px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.designation || "-"}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "4px",
                            fontSize: "11px",
                            color: "#64748b",
                          }}
                        >
                          {m.email && m.email !== "-" && (
                            <a
                              href={`mailto:${m.email}`}
                              style={{
                                color: "#38bdf8",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                textDecoration: "none",
                              }}
                              title={m.email}
                            >
                              <Mail size={11} /> Email
                            </a>
                          )}
                          {m.phone && m.phone !== "-" && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                              <Phone size={11} /> {m.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* 2. WIDGET: Attendance Card */}
        {displayType === "attendance_card" && data && (
          <div
            style={{
              marginTop: "12px",
              padding: "14px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))",
              border: "1px solid rgba(56, 189, 248, 0.3)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Clock size={16} color="#38bdf8" />
                <span style={{ fontWeight: "700", color: "#f8fafc", fontSize: "13px" }}>
                  Attendance Log ({data.date})
                </span>
              </div>
              <span
                style={{
                  fontSize: "11px",
                  padding: "3px 10px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  background:
                    data.status === "Present" || data.status === "PRESENT"
                      ? "rgba(16, 185, 129, 0.2)"
                      : data.status === "LATE" || data.is_late
                      ? "rgba(245, 158, 11, 0.2)"
                      : "rgba(239, 68, 68, 0.2)",
                  color:
                    data.status === "Present" || data.status === "PRESENT"
                      ? "#34d399"
                      : data.status === "LATE" || data.is_late
                      ? "#fbbf24"
                      : "#f87171",
                }}
              >
                {data.status}
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", textAlign: "center" }}>
              <div style={{ padding: "8px", borderRadius: "8px", background: "rgba(30, 41, 59, 0.7)" }}>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Check-In</div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#38bdf8", marginTop: "2px" }}>
                  {data.check_in || "-"}
                </div>
              </div>
              <div style={{ padding: "8px", borderRadius: "8px", background: "rgba(30, 41, 59, 0.7)" }}>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Check-Out</div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#38bdf8", marginTop: "2px" }}>
                  {data.check_out || "-"}
                </div>
              </div>
              <div style={{ padding: "8px", borderRadius: "8px", background: "rgba(30, 41, 59, 0.7)" }}>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Total Work</div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#4ade80", marginTop: "2px" }}>
                  {data.work_hours || "00:00"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3. WIDGET: Team Attendance Donut Breakdown */}
        {displayType === "chart_donut" && data && (
          <div
            style={{
              marginTop: "12px",
              padding: "14px",
              borderRadius: "12px",
              background: "rgba(15, 23, 42, 0.8)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
                fontSize: "12px",
                fontWeight: "600",
                color: "#94a3b8",
              }}
            >
              <span>Attendance Breakdown ({data.date})</span>
              <span style={{ color: "#38bdf8" }}>Total: {data.total_team_size} Members</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", textAlign: "center" }}>
              <div
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  background: "rgba(16, 185, 129, 0.15)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                }}
              >
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#34d399" }}>{data.present}</div>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Present</div>
              </div>
              <div
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  background: "rgba(239, 68, 68, 0.15)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                }}
              >
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#f87171" }}>{data.absent}</div>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Absent</div>
              </div>
              <div
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  background: "rgba(245, 158, 11, 0.15)",
                  border: "1px solid rgba(245, 158, 11, 0.3)",
                }}
              >
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#fbbf24" }}>{data.late}</div>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Late</div>
              </div>
              <div
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  background: "rgba(99, 102, 241, 0.15)",
                  border: "1px solid rgba(99, 102, 241, 0.3)",
                }}
              >
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#818cf8" }}>{data.half_day}</div>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Half Day</div>
              </div>
            </div>
          </div>
        )}

        {/* 4. WIDGET: Leave Balances */}
        {displayType === "metric_cards" && data && (
          <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
            <div
              style={{
                padding: "10px",
                borderRadius: "10px",
                background: "rgba(59, 130, 246, 0.15)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "11px", color: "#93c5fd" }}>Casual (CL)</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#60a5fa" }}>{data.casual_leave}</div>
            </div>
            <div
              style={{
                padding: "10px",
                borderRadius: "10px",
                background: "rgba(20, 184, 166, 0.15)",
                border: "1px solid rgba(20, 184, 166, 0.3)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "11px", color: "#5eead4" }}>Sick (SL)</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#2dd4bf" }}>{data.sick_leave}</div>
            </div>
            <div
              style={{
                padding: "10px",
                borderRadius: "10px",
                background: "rgba(168, 85, 247, 0.15)",
                border: "1px solid rgba(168, 85, 247, 0.3)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "11px", color: "#d8b4fe" }}>Earned (EL)</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#c084fc" }}>{data.earned_leave}</div>
            </div>
            <div
              style={{
                padding: "10px",
                borderRadius: "10px",
                background: "rgba(236, 72, 153, 0.15)",
                border: "1px solid rgba(236, 72, 153, 0.3)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "11px", color: "#f472b6" }}>Compoff</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#f472b6" }}>{data.compoff || 0}</div>
            </div>
          </div>
        )}

        {/* 5. WIDGET: Payslip Card */}
        {displayType === "payslip_card" && data && (
          <div
            style={{
              marginTop: "12px",
              padding: "14px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))",
              border: "1px solid rgba(56, 189, 248, 0.3)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontWeight: "600", color: "#38bdf8" }}>Payslip {data.period}</span>
              <span style={{ fontSize: "11px", color: "#94a3b8" }}>Days: {data.payable_days}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: "1px dashed rgba(255, 255, 255, 0.1)",
              }}
            >
              <span style={{ color: "#94a3b8" }}>Gross Earnings:</span>
              <span style={{ fontWeight: "600" }}>₹{Number(data.gross_salary || 0).toLocaleString()}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: "1px dashed rgba(255, 255, 255, 0.1)",
              }}
            >
              <span style={{ color: "#f87171" }}>Total Deductions:</span>
              <span style={{ fontWeight: "600", color: "#f87171" }}>
                -₹{Number(data.total_deductions || 0).toLocaleString()}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 0 4px 0",
                fontSize: "16px",
                fontWeight: "700",
                color: "#4ade80",
              }}
            >
              <span>Net Take-Home:</span>
              <span>₹{Number(data.net_salary || 0).toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* 6. WIDGET: Holiday List */}
        {displayType === "holiday_timeline" && data?.holidays && (
          <div
            style={{
              marginTop: "12px",
              maxHeight: "220px",
              overflowY: "auto",
              padding: "10px",
              borderRadius: "10px",
              background: "rgba(15, 23, 42, 0.7)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            {data.holidays.map((h, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                  fontSize: "12px",
                }}
              >
                <span style={{ fontWeight: "600", color: "#e2e8f0" }}>{h.holiday_name}</span>
                <span style={{ color: "#38bdf8" }}>
                  {h.holiday_date} ({h.day})
                </span>
              </div>
            ))}
          </div>
        )}

        {/* 7. WIDGET: Upcoming Celebrations */}
        {displayType === "celebrations_card" && data && (
          <div
            style={{
              marginTop: "12px",
              padding: "14px",
              borderRadius: "12px",
              background: "rgba(15, 23, 42, 0.85)",
              border: "1px solid rgba(244, 114, 182, 0.25)",
            }}
          >
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#f472b6", marginBottom: "10px" }}>
              Celebrations in {data.month}
            </div>
            {data.birthdays && data.birthdays.length > 0 && (
              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "11px", color: "#cbd5e1", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                  <Cake size={13} color="#f472b6" /> Birthdays:
                </div>
                {data.birthdays.map((b, i) => (
                  <div key={i} style={{ fontSize: "12px", color: "#94a3b8", padding: "3px 0 3px 14px" }}>
                    • {b.first_name} {b.last_name} ({b.dob}) - {b.department__name || "-"}
                  </div>
                ))}
              </div>
            )}
            {data.anniversaries && data.anniversaries.length > 0 && (
              <div>
                <div style={{ fontSize: "11px", color: "#cbd5e1", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                  <Gift size={13} color="#60a5fa" /> Work Anniversaries:
                </div>
                {data.anniversaries.map((a, i) => (
                  <div key={i} style={{ fontSize: "12px", color: "#94a3b8", padding: "3px 0 3px 14px" }}>
                    • {a.first_name} {a.last_name} ({a.date_of_joining}) - {a.department__name || "-"}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 8. WIDGET: Employee Profile Card */}
        {displayType === "profile_card" && data && (
          <div
            style={{
              marginTop: "12px",
              padding: "16px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.9))",
              border: "1px solid rgba(56, 189, 248, 0.3)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "700",
                  fontSize: "16px",
                }}
              >
                {(data.name || "E").substring(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "#f8fafc" }}>{data.name}</div>
                <div style={{ fontSize: "12px", color: "#38bdf8" }}>{data.emp_code} • {data.designation}</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "12px" }}>
              <div style={{ color: "#94a3b8" }}>Department: <strong style={{ color: "#f1f5f9" }}>{data.department}</strong></div>
              <div style={{ color: "#94a3b8" }}>Location: <strong style={{ color: "#f1f5f9" }}>{data.work_location}</strong></div>
              <div style={{ color: "#94a3b8" }}>Manager: <strong style={{ color: "#f1f5f9" }}>{data.reporting_manager}</strong></div>
              <div style={{ color: "#94a3b8" }}>Status: <strong style={{ color: "#4ade80" }}>{data.status}</strong></div>
            </div>
          </div>
        )}

        {/* 9. WIDGET: Helpdesk Tickets Table */}
        {displayType === "table_tickets" && data?.tickets && (
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              borderRadius: "10px",
              background: "rgba(15, 23, 42, 0.8)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {data.tickets.map((t, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                  fontSize: "12px",
                }}
              >
                <div>
                  <span style={{ fontWeight: "700", color: "#38bdf8", marginRight: "8px" }}>#{t.ticket_number}</span>
                  <span style={{ color: "#e2e8f0" }}>{t.subject}</span>
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <span
                    style={{
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      background: "rgba(245, 158, 11, 0.2)",
                      color: "#fbbf24",
                    }}
                  >
                    {t.priority}
                  </span>
                  <span
                    style={{
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      background: "rgba(99, 102, 241, 0.2)",
                      color: "#818cf8",
                    }}
                  >
                    {t.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 10. WIDGET: Leave Requests Table */}
        {displayType === "table_leaves" && data?.leaves && (
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              borderRadius: "10px",
              background: "rgba(15, 23, 42, 0.8)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {data.leaves.map((l, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                  fontSize: "12px",
                }}
              >
                <div>
                  <span style={{ fontWeight: "700", color: "#60a5fa", marginRight: "6px" }}>{l.leave_type}</span>
                  <span style={{ color: "#94a3b8" }}>{l.from_date} to {l.to_date} ({l.total_days}d)</span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    background:
                      l.leave_status === "Approved"
                        ? "rgba(16, 185, 129, 0.2)"
                        : l.leave_status === "Rejected"
                        ? "rgba(239, 68, 68, 0.2)"
                        : "rgba(245, 158, 11, 0.2)",
                    color:
                      l.leave_status === "Approved"
                        ? "#34d399"
                        : l.leave_status === "Rejected"
                        ? "#f87171"
                        : "#fbbf24",
                  }}
                >
                  {l.leave_status}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Sources / RAG Citations */}
        {message.sources && message.sources.length > 0 && (
          <div
            style={{
              marginTop: "12px",
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              paddingTop: "8px",
              fontSize: "11px",
              color: "#94a3b8",
            }}
          >
            <strong>Referenced Knowledge:</strong>
            <ul style={{ paddingLeft: "16px", margin: "4px 0 0 0" }}>
              {message.sources.map((s, idx) => (
                <li key={idx}>
                  📄 {s.filename} {s.page ? `(Page ${s.page})` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Timestamp */}
        {message.time && (
          <div
            style={{
              marginTop: "8px",
              fontSize: "10px",
              opacity: 0.6,
              textAlign: "right",
            }}
          >
            {message.time}
          </div>
        )}
      </div>

      {isUser && (
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #10b981, #059669)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
          }}
        >
          <User size={20} />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;